import { useEffect, useState } from 'react';
import { liensPublicsApi } from '../api/resources';
import { useBoiteEntreprise } from '../lib/boite-entreprise';
import { tr, trf } from '../lib/i18n';
import { C } from '../theme/palette';
import { useLienClient } from './RelanceParLien.jsx';
import { Bar } from './ui/Bar.jsx';
import { Btn } from './ui/Btn.jsx';
import { Card } from './ui/Card.jsx';

const bouton = (fond) => ({
  background: fond,
  color: '#fff',
  borderRadius: 9,
  padding: 10,
  textAlign: 'center',
  fontSize: 10,
  fontWeight: 700,
});

/** « Ouvert 2 fois », d'après ce que le serveur a compté. */
function consultations(lien, ouvertures) {
  const nombre = ouvertures ? ouvertures.nombre : lien?.consultations;
  if (!nombre) {
    // Seulement le PDF, par le bouton de l'e-mail : la pièce a bien été ouverte.
    if (ouvertures?.pdfOuverts > 0) return trf('PDF ouvert {0} fois', ouvertures.pdfOuverts);
    if (ouvertures?.vus > 0) return tr('Vue dans l’e-mail (probablement)');
    return 'Pas encore ouvert par le client';
  }
  // Plusieurs personnes l'ont ouvert : le compte n'est plus celui du seul client.
  if (ouvertures?.personnes > 1) return trf('Ouvert {0} fois', nombre);
  return nombre === 1
    ? 'Ouvert 1 fois par le client'
    : trf('Ouvert {0} fois par le client', nombre);
}

/** « 15.10.2026 à 21:12 », à l'heure de Suisse. */
/** Une ligne du détail : « 19.10.2026 à 14:10 · 📥 PDF téléchargé · autre personne (Android) ». */
function ligneEvenement(e) {
  if (e.nature === 'TRANSMISSION') {
    return (
      quandOuvert(e.instant) +
      ' · ' +
      trf('📤 transmis à {0}', e.transmisNom || '') +
      (e.transmisEmail ? ' (' + e.transmisEmail + ')' : '') +
      (e.nom ? ' · ' + trf('par {0}', e.nom) : '')
    );
  }
  const quoi =
    e.nature === 'VU'
      ? tr('✉️ vue dans l’e-mail (probablement)')
      : e.nature === 'TELECHARGEMENT'
        ? tr('📥 PDF téléchargé')
        : e.nature === 'PDF'
          ? tr('📄 PDF ouvert')
          : e.nature === 'CAPTURE'
            ? tr('📸 capture d’écran')
            : e.depuisPdf
              ? tr('📄 ouvert depuis le PDF')
              : tr('👁 ouvert');
  // Le nom que la personne a donné, sinon « autre personne n° 2 ».
  const personne = e.nom || (e.personne > 1 ? trf('autre personne n° {0}', e.personne) : '');
  const qui = personne
    ? ' · ' + personne + (e.appareil ? ' (' + e.appareil + ')' : '')
    : e.appareil
      ? ' · ' + e.appareil
      : '';
  return quandOuvert(e.instant) + ' · ' + quoi + qui;
}

/**
 * Les trois chiffres du suivi : « combien de fois il a regardé, s'il a
 * téléchargé, s'il a transmis ». Regardée compte la page en ligne, le PDF
 * ouvert et l'aperçu A4 vu dans l'e-mail.
 *
 * @param {{ouvertures: object|null}} props ouvertures de la pièce
 * @returns {import('react').ReactElement|null} les trois cases, ou rien
 */
export function TroisChiffres({ ouvertures }) {
  if (!ouvertures) return null;
  const evenements = ouvertures.evenements || [];
  const regardee = (ouvertures.nombre || 0) + (ouvertures.pdfOuverts || 0) + (ouvertures.vus || 0);
  const telechargee = ouvertures.telechargements || 0;
  const transmise =
    ouvertures.transmissions ?? evenements.filter((e) => e.nature === 'TRANSMISSION').length;
  const cases = [
    ['👁', regardee, tr('fois regardée')],
    ['📥', telechargee, tr('fois téléchargée')],
    ['📤', transmise, tr('fois transmise')],
  ];
  return (
    <div
      data-suivi="trois-chiffres"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 6,
        margin: '6px 0 8px',
      }}
    >
      {cases.map(([icone, n, libelle]) => (
        <div
          key={libelle}
          style={{
            background: C.navyXL,
            borderRadius: 10,
            padding: '8px 4px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 900, color: C.ink }}>
            {icone} {n}
          </div>
          <div style={{ fontSize: 8.5, color: C.grey }}>{libelle}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Ce que le client a fait de sa pièce, au-delà de l'ouvrir : télécharger le
 * PDF, et le transmettre — un autre appareil a ouvert le lien.
 *
 * @param {{ouvertures: object|null}} props ouvertures de la pièce
 * @returns {import('react').ReactElement|null} les deux lignes, ou rien
 */
function SuiviPdfEtTransmission({ ouvertures }) {
  if (!ouvertures) return null;
  const evenements = ouvertures.evenements || [];
  const dernierPdf = evenements.find((e) => e.nature === 'TELECHARGEMENT');
  // Ouvert par le bouton « Ouvrir … en PDF » de l'e-mail d'envoi.
  const pdfCourriel = evenements.find((e) => e.nature === 'PDF');
  // Ouvert par le bouton « Voir en ligne » du PDF reçu.
  const depuisPdf = evenements.filter((e) => e.nature === 'OUVERTURE' && e.depuisPdf);
  const autre = evenements.find((e) => e.personne > 1 && e.nature !== 'TRANSMISSION');
  const transmissions = evenements.filter((e) => e.nature === 'TRANSMISSION');
  // Les autres personnes qui ont ouvert la pièce et ont un nom.
  const nommees = [
    ...new Set(
      evenements
        .filter((e) => e.personne > 1 && e.nom && e.nature !== 'TRANSMISSION')
        .map((e) => e.nom),
    ),
  ];
  if (
    !ouvertures.pdfOuverts &&
    !ouvertures.telechargements &&
    !(ouvertures.personnes > 1) &&
    !transmissions.length &&
    !depuisPdf.length
  ) {
    return null;
  }
  return (
    <div style={{ fontSize: 9, color: C.ink, marginBottom: 6, lineHeight: 1.6 }}>
      {ouvertures.pdfOuverts > 0 && (
        <div data-suivi="pdf-courriel" style={{ fontWeight: 700 }}>
          📄{' '}
          {trf(
            'PDF ouvert {0} fois · dernier : {1}',
            ouvertures.pdfOuverts,
            pdfCourriel ? quandOuvert(pdfCourriel.instant) : '—',
          )}
        </div>
      )}
      {depuisPdf.length > 0 && (
        <div data-suivi="depuis-pdf">
          📄{' '}
          {trf(
            'Ouvert {0} fois depuis le PDF · dernière : {1}',
            depuisPdf.length,
            quandOuvert(depuisPdf[0].instant),
          )}
        </div>
      )}
      {ouvertures.telechargements > 0 && (
        <div data-suivi="telechargement">
          📥{' '}
          {trf(
            'PDF téléchargé {0} fois · dernier : {1}',
            ouvertures.telechargements,
            dernierPdf ? quandOuvert(dernierPdf.instant) : '—',
          )}
        </div>
      )}
      {ouvertures.personnes > 1 && (
        <div data-suivi="transmis" style={{ color: C.purple, fontWeight: 700 }}>
          👥{' '}
          {trf(
            'Transmis : ouvert par {0} personnes · dernière autre personne : {1}',
            ouvertures.personnes,
            autre
              ? quandOuvert(autre.instant) + (autre.appareil ? ' (' + autre.appareil + ')' : '')
              : '—',
          )}
          {nommees.length > 0 && <div>👤 {nommees.join(', ')}</div>}
        </div>
      )}
      {transmissions.map((t, i) => (
        <div key={i} data-suivi="transmission" style={{ color: C.purple, fontWeight: 700 }}>
          📤{' '}
          {trf(
            'Transmis par e-mail à {0} · {1}',
            (t.transmisNom || '') + (t.transmisEmail ? ' (' + t.transmisEmail + ')' : ''),
            quandOuvert(t.instant),
          )}
        </div>
      ))}
    </div>
  );
}

export function quandOuvert(instant) {
  const d = new Date(instant);
  if (Number.isNaN(d.getTime())) return '';
  const jour = d.toLocaleDateString('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Zurich',
  });
  const heure = d.toLocaleTimeString('fr-CH', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Zurich',
  });
  return trf('{0} à {1}', jour, heure);
}

/**
 * Carte du lien par lequel un client consulte sa pièce.
 *
 * <p>Le lien affiché ici se composait à l'écran et ne menait nulle part. Il
 * vient du serveur, qui dit aussi si le client l'a ouvert, et se retire d'un
 * geste : un lien envoyé au mauvais destinataire cesse aussitôt de s'ouvrir.
 *
 * @param {object} props propriétés du composant
 * @param {'devis'|'facture'|'livraison'} props.type type de la pièce
 * @param {number|string} props.id identifiant de la pièce
 * @param {string} props.titre titre de la carte
 * @param {string} [props.clientNom] nom du client, pour le message
 * @param {string} [props.email] adresse du client
 * @param {string} [props.tel] numéro du client, au format international sans « + »
 * @param {string} props.sujet objet du courriel
 * @param {(lien: string) => string} props.message texte, le lien y prenant place
 * @param {boolean} [props.sansCanaux] sans boutons E-mail ni WhatsApp : l'offre et la
 *   facture s'envoient par « Envoyer », en un seul endroit
 * @param {boolean} [props.suiviSeul] seulement le suivi : quand le client a ouvert la
 *   pièce, sans le lien ni ses boutons (offre et facture, à la demande du patron)
 * @param {import('react').ReactNode} [props.children] contenu ajouté sous les boutons
 * @returns {JSX.Element} la carte
 */
export function CarteLienClient({
  type,
  id,
  titre,
  clientNom,
  email,
  tel,
  sujet,
  message,
  sansCanaux,
  suiviSeul,
  children,
}) {
  const [version, setVersion] = useState(0);
  // Boîte de l'entreprise raccordée : l'offre part par « Envoyer », de son adresse.
  const boiteEntreprise = useBoiteEntreprise();
  const { lien, pret } = useLienClient(type, id, version);
  const [copie, setCopie] = useState(false);
  // Chaque ouverture par le client, avec sa date et son heure : offres et
  // factures seulement, dont le serveur garde le détail.
  const [ouvertures, setOuvertures] = useState(null);
  const [detail, setDetail] = useState(false);
  const suivi = (type === 'devis' || type === 'facture') && typeof id === 'number';
  useEffect(() => {
    if (!suivi || !lien) return undefined;
    let vivant = true;
    Promise.resolve()
      .then(() => liensPublicsApi.ouvertures(type, id))
      .then((o) => {
        if (vivant && o) setOuvertures(o);
      })
      .catch(() => {});
    return () => {
      vivant = false;
    };
  }, [suivi, lien, type, id]);
  const texte = lien && message ? message(lien.url) : '';
  if (suiviSeul) {
    if (!lien) return null;
    return (
      <Card style={{ marginTop: 11 }}>
        <Bar icon="👁" title={tr('Consultations par tes clients')} color={C.teal} />
        <div style={{ padding: 13 }}>
          <div style={{ fontSize: 9, color: C.muted, marginBottom: 4 }}>
            <b>{clientNom || tr('Le client')}</b> · {consultations(lien, ouvertures)}
          </div>
          <TroisChiffres ouvertures={ouvertures} />
          {(ouvertures?.instants?.length > 0 || ouvertures?.evenements?.length > 0) && (
            <div style={{ fontSize: 9, color: C.ink, lineHeight: 1.5 }}>
              {ouvertures.instants?.length > 0 && (
                <>👁 {trf('Dernière ouverture : {0}', quandOuvert(ouvertures.instants[0]))}</>
              )}
              <ol
                aria-label="Ouvertures par le client"
                style={{ margin: '4px 0 0', paddingLeft: 18, color: C.grey }}
              >
                {(ouvertures.evenements?.length
                  ? ouvertures.evenements
                  : ouvertures.instants.map((i) => ({ instant: i, nature: 'OUVERTURE' }))
                ).map((e, k) => (
                  <li key={e.instant + '-' + k}>{ligneEvenement(e)}</li>
                ))}
              </ol>
            </div>
          )}
          <SuiviPdfEtTransmission ouvertures={ouvertures} />
        </div>
      </Card>
    );
  }
  return (
    <Card style={{ marginTop: 11 }}>
      <Bar icon="🔗" title={titre} color={C.teal} sub="Lien personnel, à remettre au client" />
      <div style={{ padding: 13 }}>
        {!pret && (
          <div style={{ fontSize: 8, color: C.muted }} role="status">
            Préparation du lien…
          </div>
        )}
        {pret && !lien && (
          <div style={{ fontSize: 8, color: C.muted, lineHeight: 1.5 }}>
            {typeof id === 'number'
              ? 'Le lien n’a pas pu être préparé. Rechargez la page pour réessayer.'
              : 'Le lien sera disponible dès que la pièce sera enregistrée.'}
          </div>
        )}
        {lien && (
          <>
            <div style={{ fontSize: 8, color: C.muted, marginBottom: 4 }}>
              Lien de <b>{clientNom || 'ce client'}</b> · {consultations(lien, ouvertures)}
            </div>
            <TroisChiffres ouvertures={ouvertures} />
            {ouvertures?.instants?.length > 0 && (
              <div style={{ fontSize: 9, color: C.ink, marginBottom: 6, lineHeight: 1.5 }}>
                👁 {trf('Dernière ouverture : {0}', quandOuvert(ouvertures.instants[0]))}
                {ouvertures.instants.length > 1 && (
                  <button
                    onClick={() => setDetail((v) => !v)}
                    style={{
                      marginLeft: 6,
                      border: 'none',
                      background: 'none',
                      color: C.teal,
                      fontSize: 9,
                      fontWeight: 700,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    {detail ? 'Masquer ▴' : 'Toutes les ouvertures ▾'}
                  </button>
                )}
                {detail && (
                  <ol
                    aria-label="Ouvertures par le client"
                    style={{ margin: '4px 0 0', paddingLeft: 18, color: C.grey }}
                  >
                    {(ouvertures.evenements?.length
                      ? ouvertures.evenements
                      : ouvertures.instants.map((i) => ({ instant: i, nature: 'OUVERTURE' }))
                    ).map((e, k) => (
                      <li key={e.instant + '-' + k}>{ligneEvenement(e)}</li>
                    ))}
                  </ol>
                )}
              </div>
            )}
            <SuiviPdfEtTransmission ouvertures={ouvertures} />
            <div
              style={{
                background: C.navyXL,
                borderRadius: 9,
                padding: '10px 12px',
                fontSize: 9,
                color: C.grey,
                wordBreak: 'break-all',
                marginBottom: 8,
                fontFamily: 'monospace',
              }}
            >
              {lien.url}
            </div>
            <div style={{ marginBottom: 8 }}>
              <Btn
                label={copie ? '✓ Lien copié' : '📋 Copier le lien'}
                sm
                color={copie ? C.green : C.teal}
                full
                onClick={() => {
                  try {
                    navigator.clipboard && navigator.clipboard.writeText(lien.url);
                  } catch (e) {
                    // Presse-papiers refusé : le lien reste lisible à l'écran.
                  }
                  setCopie(true);
                  setTimeout(() => setCopie(false), 1500);
                }}
              />
            </div>
            {!sansCanaux && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: boiteEntreprise ? '1fr' : '1fr 1fr',
                  gap: 8,
                }}
              >
                {!boiteEntreprise && (
                  <a
                    href={
                      'mailto:' +
                      (email || '') +
                      '?subject=' +
                      encodeURIComponent(sujet) +
                      '&body=' +
                      encodeURIComponent(texte)
                    }
                    style={{ textDecoration: 'none' }}
                  >
                    <div style={bouton(C.navy)}>✉ Email</div>
                  </a>
                )}
                <a
                  href={'https://wa.me/' + (tel || '') + '?text=' + encodeURIComponent(texte)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <div style={bouton(C.green)}>WhatsApp</div>
                </a>
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <Btn
                label="🚫 Retirer ce lien"
                sm
                ghost
                color={C.grey}
                full
                onClick={() =>
                  liensPublicsApi
                    .revoque(type, id)
                    .then(() => setVersion((v) => v + 1))
                    .catch(() => {})
                }
              />
              <div style={{ fontSize: 8, color: C.muted, marginTop: 5, lineHeight: 1.5 }}>
                Le lien retiré cesse de s’ouvrir ; un nouveau le remplace, à renvoyer au client.
              </div>
            </div>
          </>
        )}
        {children && <div style={{ marginTop: 10 }}>{children}</div>}
      </div>
    </Card>
  );
}
